import { useMemo, useState } from 'react';
import { Card, Chip } from '@/shared/ui/atoms';
import { CopyButton } from '@/features/onboarding/components/CopyButton';
import { LINK_GROUPS, type DevLink, type Env } from '@/features/onboarding/data/links';

const ENV_LABEL: Record<Env, string> = { prod: '운영', dev: '개발', both: '공통' };

function EnvChip({ env }: { env: Env }): React.JSX.Element {
  // 운영 주소는 눈에 띄어야 한다 — 개발이라고 생각하고 누르는 사고가 여기서 난다.
  const skin =
    env === 'prod'
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : env === 'dev'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : 'border-line bg-canvas text-muted';
  return <span className={`rounded-full border px-1.5 text-[11px] whitespace-nowrap ${skin}`}>{ENV_LABEL[env]}</span>;
}

function LinkRow({ link }: { link: DevLink }): React.JSX.Element {
  return (
    <li className="rounded-md border border-line-soft px-3 py-2 hover:border-accent-line">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] font-semibold text-accent hover:underline"
        >
          {link.label} ↗
        </a>
        {link.env && <EnvChip env={link.env} />}
        {link.access && <Chip tone="warn">권한 · {link.access}</Chip>}
        <span className="ml-auto">
          <CopyButton value={link.url} />
        </span>
      </div>
      <div className="text-[12px] text-muted">{link.note}</div>
      <div className="truncate font-mono text-[11px] text-faint">{link.url}</div>
    </li>
  );
}

/**
 * 링크 모음 — 신입이 가장 먼저 필요로 하는 화면. 검색 하나로 전 그룹을 훑는다.
 * 주소 자체가 정본이 아니라 `data/links.ts`가 정본이다(화면은 그걸 그리기만 한다).
 */
export function LinksView(): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [env, setEnv] = useState<'all' | 'prod' | 'dev'>('all');

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return LINK_GROUPS.map((group) => ({
      ...group,
      links: group.links.filter((link) => {
        // 'both'는 어느 환경 필터에도 남는다 — 환경을 가리지 않는 도구까지 숨기면 검색이 아니라 함정이 된다.
        const envOk = env === 'all' || link.env === undefined || link.env === 'both' || link.env === env;
        const text = `${link.label} ${link.url} ${link.note}`.toLowerCase();
        return envOk && (needle === '' || text.includes(needle));
      }),
    })).filter((group) => group.links.length > 0);
  }, [query, env]);

  const total = groups.reduce((sum, group) => sum + group.links.length, 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface px-4 py-3">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="이름·주소·설명 검색 (예: grafana, 어드민)"
          className="min-w-[240px] flex-1 rounded-md border border-line px-2.5 py-1.5 text-[13px] outline-none focus:border-accent"
        />
        <div className="flex gap-1">
          {(['all', 'dev', 'prod'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setEnv(key)}
              className={`cursor-pointer rounded-md border px-2.5 py-1.5 text-[12px] ${
                env === key ? 'border-accent-line bg-accent-soft font-semibold text-accent' : 'border-line text-muted hover:bg-line-soft'
              }`}
            >
              {key === 'all' ? '전체' : key === 'dev' ? '개발' : '운영'}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-faint">{total}개</span>
      </div>

      {groups.map((group) => (
        <Card key={group.key} title={group.title} hint={group.hint}>
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(420px,1fr))] gap-2">
            {group.links.map((link) => (
              <LinkRow key={link.url + link.label} link={link} />
            ))}
          </ul>
        </Card>
      ))}

      {groups.length === 0 && (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-muted">
          🔍 검색 결과가 없습니다 — 없는 링크라면 `console-web/src/features/onboarding/data/links.ts`에 추가해 주세요
        </div>
      )}
    </div>
  );
}
